import React, { useEffect, useState, useMemo, useRef, useCallback } from "react";
import { Accordion, Row, Col, Form } from "react-bootstrap";
import TopNav from "../../TopNav/TopNav";
import JobNav from "../JobNav";
import JobGeneralOverview from "../JobOverviewBar";
import { mapUserRoles } from "../../../functions/useRoleMapping";
import { useSelector } from "react-redux";
import { selectApiUrls } from "../../../redux/slices/apiUrlSlice";
import { selectUserDetails, selectUserRoles } from "../../../redux/slices/userSlice";
import { useLocation } from "react-router-dom";
import {
  selectGeneralStation,
  selectPumpManifoldPiping,
  selectSkidConstrution,
  selectStationEnclosure,
  selectDropPipeComments,
  selectLevelControls,
  selectPumps,
  selectWaterTreatment,
  selectFertigation,
  selectSkyHarvester,
  selectShipLoose,
} from "../../../redux/selectors";
import CheckoutNotification from "../checkoutNotification";
import HCRequirements from "./HCRequirements";
import SkidConstruction from "./SkidConstruction";
import PumpManifoldPiping from "./PumpManifoldPiping";
import StationEnclosure from "./StationEnclosure";
import DropPipes from "./DropPipes";
import Pumps from "./Pumps/Pumps";
import LevelControls from "./LevelControls";
import Filtration from "./Filtration/Filtration";
import Fertigation from "./Fertigation";
import SkyHarvester from "./SkyHarvester";
import ShipLoose from "./ShipLoose";
import ShipEarlyForm from "../../ShipEarlyForm";
import useHandleBlur from "../../../functions/useHandleBlur";
import SaveBanner from "./SaveBanner";
import { normalizeToggleValue } from "../../../utils/toggleValues";
import useJobHandler, { useJobSync } from "../../../functions/JobHandler"; // Import the useJobHandler function
import { selectApiStatus } from "../../../redux/slices/apiStatusSlice";
import { selectFullJobFetchLoading } from "../../../redux/slices/fullJobFetchSlice";
import ZoneButtons from "../ZoneButtons";
import InfoBubbleModal from "../../common/InfoBubbleModal";
import { getZoneItemCount, getShipEarlyItemCount } from "../../../utils/itemCountUtils";
import useCriticalData from "../../../functions/useCriticalData";

// Add selector for shipEarlyParent
const EMPTY_ARRAY = [];
const selectShipEarlyParent = (state) => state.jobDetails.shipEarlyParent || EMPTY_ARRAY;

function JobMechSpecs() {
  const currentKey = "mechInfo";
  const userDetails = useSelector(selectUserDetails);
  const currentUser = userDetails?.name;
  const userRoles = useSelector(selectUserRoles); // User details
  const [roles, setRoles] = useState(mapUserRoles(userRoles));
  const apiStatus = useSelector(selectApiStatus); // Get API status from Redux
  const fullJobLoading = useSelector(selectFullJobFetchLoading); // Get fullJobFetch loading state

  const jobId = useSelector((state) => state.jobId.jobId);
  const apiUrls = useSelector(selectApiUrls);
  const readOnly = useSelector((state) => state.readOnly.isReadOnly); // Fetch read-only state from Redux
  
  // Ensure critical data (including dropdown values) is loaded
  useCriticalData({
    autoLoad: true,
    includeUserDropdowns: true
  });
  // Check if APIs are still loading
  const [apisLoading, setApisLoading] = useState(true);
  
  // Arrays of API sections that need to be loaded
  const requiredApis = useMemo(() => [
    'generalStation', 
    'skidConstruction', 
    'pumpManifoldPiping', 
    'stationEnclosure', 
    'dropPipeComments', 
    'levelControls', 
    'pumps', 
    'waterTreatment', 
    'fertigation', 
    'skyHarvester', 
    'shipLoose',
    'shipEarlyParent'
  ], []);

  // Check API loading status
  useEffect(() => {
    // Function to check if required APIs are loaded
    const checkApisStatus = () => {
      const statusKeys = Object.keys(apiStatus);

      // If fullJobFetch is actively loading, show loading state
      if (fullJobLoading) {
        // If no section statuses exist yet, still loading initial payload
        if (statusKeys.length === 0) {
          return true;
        }

        // If any required section has already settled, don't let global loading pin the page
        const hasSettledSection = requiredApis.some(
          (api) => apiStatus[api] === "success" || apiStatus[api] === "failed"
        );

        if (!hasSettledSection) {
          return true;
        }
      }

      // If apiStatus is empty and not loading, APIs haven't started yet
      if (statusKeys.length === 0) {
        return false;
      }

      // Check if any required API is still pending
      const stillLoading = requiredApis.some(api => 
        apiStatus[api] === 'pending'
      );
      
      return stillLoading;
    };

    setApisLoading(checkApisStatus());
  }, [apiStatus, requiredApis, fullJobLoading]);
  
  useEffect(() => {
    setRoles(mapUserRoles(userRoles));
  }, [userRoles]);

  const generalSpecsDetails = useSelector(selectGeneralStation);
  const skidConstructionDetails = useSelector(selectSkidConstrution);
  const pumpManifoldPipingDetails = useSelector(selectPumpManifoldPiping);
  const stationEnclosureDetails = useSelector(selectStationEnclosure);
  const dropPipeCommentDetails = useSelector(selectDropPipeComments);
  const levelControlsDetails = useSelector(selectLevelControls);
  const pumpDetails = useSelector(selectPumps);
  const waterTreatment = useSelector(selectWaterTreatment);
  const fertigationDetails = useSelector(selectFertigation);
  const skyHarvesterDetails = useSelector(selectSkyHarvester);
  const shipLooseDetails = useSelector(selectShipLoose);
  const shipEarlyParentDetails = useSelector(selectShipEarlyParent);


  const tbdForms = useSelector((state) => state.tbd?.forms || {});

  useJobHandler(); // Call the useJobHandler function
  
  // Sync job data when this page loads
  const { syncJob } = useJobSync();
  const enableMountSync = process.env.REACT_APP_ENABLE_MOUNT_SYNC === "true";
  
  useEffect(() => {
    if (jobId && enableMountSync) {
      console.log("JobMechInfo: Syncing job data for jobId:", jobId);
      syncJob({ reuseRecent: true })
        .then((result) => {
          if (result && result.fieldsUpdated > 0) {
            console.log(`JobMechInfo: Updated ${result.fieldsUpdated} fields across ${result.sectionsUpdated} sections`);
          } else {
            console.log("JobMechInfo: Job data is already up to date");
          }
        })
        .catch((error) => {
          console.error("JobMechInfo: Failed to sync job data:", error);
        });
    }
  }, [jobId, syncJob, enableMountSync]);
  
  const activeTabNumber = useSelector(
    (state) => state.activeMechanicalTab.selectedZone || 1 // Ensure default to 1
  );
  
  const [, setActiveTab] = useState(`zone${activeTabNumber}`);
  useEffect(() => {
    // Keep activeTab in sync with activeTabNumber
    setActiveTab(`zone${activeTabNumber}`);
    // Reset userModifiedToggles when zone changes so state gets re-initialized
    userModifiedToggles.current.clear();
  }, [activeTabNumber]);
  const [isHcTrackerType, setIsHcTrackerType] = useState(false);
  const [accordionToggles, setAccordionToggles] = useState({});
  const [sectionDetails, setSectionDetails] = useState({});
  
  // Info bubble modal states
  const [showInfoBubbleModal, setShowInfoBubbleModal] = useState(false);
  const [infoBubbleSection, setInfoBubbleSection] = useState({ sectionName: '', value: '', title: '' });
  
  // Track which toggles have been manually changed by the user
  const userModifiedToggles = useRef(new Set());

  const {
    handleBlur,
    showNotification,
    saveStatus,
  } = useHandleBlur();

  const [loadingSections] = useState({
    generalSpecsDetails: true,

  });

  useEffect(() => {
    if (generalSpecsDetails.length > 0) {
      const matchingSpecs = generalSpecsDetails.find(
        (spec) => spec.Zone === activeTabNumber
      );
      setIsHcTrackerType(matchingSpecs?.TrackerType === "HC" || false);
    }
  }, [generalSpecsDetails, activeTabNumber]);

  // Define all sections centrally
  const sections = useMemo(
    () => [
      {
        name: "Skid Construction",
        component: SkidConstruction,
        hasToggle: true,
        apiUrl: apiUrls.skidConstruction,
        field: "HasSkid",
      },      {
        name: "Pump Column / Manifold / Piping",
        component: PumpManifoldPiping,
        hasToggle: true,
        apiUrl: apiUrls.pumpManifoldPiping,
        field: "HasPiping",
      },
      {
        name: "Station Enclosure",
        component: StationEnclosure,
        hasToggle: true,
        apiUrl: apiUrls.stationEnclosure,
        field: "HasEnclosure",
      },
      {
        name: "Drop Pipes",
        component: DropPipes,
        hasToggle: true,
        apiUrl: apiUrls.dropPipeComments,
        field: "HasDropPipes",

      },
      {
        name: "Pumps",
        component: Pumps,
        hasToggle: true,
        apiUrl: apiUrls.pumps,
        field: "HasMain",
      },
      {
        name: "Level Controls",
        component: LevelControls,
        hasToggle: true,
        apiUrl: apiUrls.levelControls,
        field: "HasLevelControls",
      },
      {
        name: "Water Treatment",
        component: Filtration,
        hasToggle: true,
        apiUrl: apiUrls.waterTreatment,
        field: "HasFilters",
      },
      {
        name: "Fertigation",
        component: Fertigation,
        hasToggle: true,
        apiUrl: apiUrls.fertigation,
        field: "HasFertigation",
      },
      {
        name: "Sky Harvester",
        component: SkyHarvester,
        hasToggle: true,
        apiUrl: apiUrls.skyHarvester,
        field: "HasSky",
      },
      {
        name: "Ship Loose",
        component: ShipLoose,
        hasToggle: true,
        apiUrl: apiUrls.shipLoose,
        field: "HasShipLoose",
      },
      {
        name: "Ship Early/ Direct Form",
        component: ShipEarlyForm,
        hasToggle: false, // No toggle for Ship Early/ Direct Form
        apiUrl: apiUrls.shipEarlyParent,
        field: "HasShipEarly",
        isSpecial: true, // Mark as special to handle differently
      },
    ],
    []
  );  // Set initial state for toggles based on skid construction details
  useEffect(() => {
    // Only initialize if APIs are done loading
    if (apisLoading) {
      return;
    }

    // Generate initial state dynamically with default "no" values
    const initialToggles = sections.reduce((acc, section) => {
      acc[section.name] = "no";
      return acc;
    }, {});

    // Generate initial details object with IDs based on section details
    const initialDetails = {};

    // Find the value for "Skid Construction" based on the skidConstructionDetails
    if (skidConstructionDetails.length > 0) {
      const matchingSkid = skidConstructionDetails.find(
        (skid) => skid.Zone === activeTabNumber
      );
      if (matchingSkid) {
        initialToggles["Skid Construction"] = normalizeToggleValue(matchingSkid.HasSkid)
          ? "yes"
          : "no";
        initialDetails["Skid Construction"] = matchingSkid.ID; // Assuming ProjectID is the ID we need
      }
    }    if (pumpManifoldPipingDetails.length > 0) {
      const matchingPumpManifoldPiping = pumpManifoldPipingDetails.find(
        (pmp) => pmp.Zone === activeTabNumber
      );
      if (matchingPumpManifoldPiping) {
        initialToggles["Pump Column / Manifold / Piping"] =
          normalizeToggleValue(matchingPumpManifoldPiping.HasPiping) ? "yes" : "no";
        initialDetails["Pump Column / Manifold / Piping"] =
          matchingPumpManifoldPiping.ID; // Assuming ProjectID is the ID we need
      }
    }
    if (stationEnclosureDetails.length > 0) {
      const matchingStationEnclosure = stationEnclosureDetails.find(
        (enclosure) => enclosure.Zone === activeTabNumber
      );
      if (matchingStationEnclosure) {
        initialToggles["Station Enclosure"] =
          normalizeToggleValue(matchingStationEnclosure.HasEnclosure) ? "yes" : "no";
        initialDetails["Station Enclosure"] = matchingStationEnclosure.ID; // Assuming ProjectID is the ID we need
      }
    }
    if (dropPipeCommentDetails.length > 0) {
      const matchingDropPipeComments = dropPipeCommentDetails.find(
        (droppipe) => droppipe.Zone === activeTabNumber
      );
      if (matchingDropPipeComments) {
        initialToggles["Drop Pipes"] =
        normalizeToggleValue(matchingDropPipeComments.HasDropPipes) ? "yes" : "no";
        initialDetails["Drop Pipes"] = matchingDropPipeComments.ID; // Assuming ProjectID is the ID we need
      }
    }
    if (levelControlsDetails.length > 0) {
      const matchingLevelControls = levelControlsDetails.find(
        (levelcontrol) => levelcontrol.Zone === activeTabNumber
      );
      if (matchingLevelControls) {
        initialToggles["Level Controls"] =
        normalizeToggleValue(matchingLevelControls.HasLevelControls) ? "yes" : "no";
        initialDetails["Level Controls"] = matchingLevelControls.ID; // Assuming ProjectID is the ID we need
      }
    }
    if (pumpDetails.length > 0) {
      const matchingPumps = pumpDetails.find(
        (pump) => pump.Zone === activeTabNumber
      );
      if (matchingPumps) {
        initialToggles["Pumps"] =
        normalizeToggleValue(matchingPumps.HasMain) ? "yes" : "no";
        initialDetails["Pumps"] = matchingPumps.ID; // Assuming ProjectID is the ID we need
      }
    }
    if (waterTreatment.length > 0) {
      const matchingFilters = waterTreatment.find(
        (filter) => filter.Zone === activeTabNumber
      );
      if (matchingFilters) {
     
        initialToggles["Water Treatment"] =
        normalizeToggleValue(matchingFilters.HasFilters) ? "yes" : "no";
        initialDetails["Water Treatment"] = matchingFilters.ID; // Assuming ProjectID is the ID we need
      }
    }
    if (fertigationDetails.length > 0) {
      const matchingFertigation = fertigationDetails.find(
      (fertigation) => fertigation.Zone === activeTabNumber
      );
      if (matchingFertigation) {
      initialToggles["Fertigation"] =
        normalizeToggleValue(matchingFertigation.HasFertigation) ? "yes" : "no";
      initialDetails["Fertigation"] = matchingFertigation.ID; // Assuming ProjectID is the ID we need
      }
    }
    if (skyHarvesterDetails.length > 0) {
      const matchingSkyHarvester = skyHarvesterDetails.find(
      (skyHarvester) => skyHarvester.Zone === activeTabNumber
      );
      if (matchingSkyHarvester) {
      initialToggles["Sky Harvester"] =
        normalizeToggleValue(matchingSkyHarvester.HasSky) ? "yes" : "no";
      initialDetails["Sky Harvester"] = matchingSkyHarvester.ID; // Assuming ProjectID is the ID we need
      }
    }
    if (shipLooseDetails.length > 0) {
      const matchingShipLoose = shipLooseDetails.find(
      (shipLoose) => shipLoose.Zone === activeTabNumber
      );
      if (matchingShipLoose) {
      initialToggles["Ship Loose"] =
        normalizeToggleValue(matchingShipLoose.HasShipLoose) ? "yes" : "no";
      initialDetails["Ship Loose"] = matchingShipLoose.ID; // Assuming ProjectID is the ID we need
      }
    }

    // Check if Ship Early/ Direct Form should be enabled based on shipEarlyParent records
    if (shipEarlyParentDetails.length > 0) {
      // Check if any record matches the current jobId
      const hasShipEarlyRecord = shipEarlyParentDetails.some(
        (record) => String(record.ProjectID) === String(jobId)
      );
      initialToggles["Ship Early/ Direct Form"] = hasShipEarlyRecord ? "yes" : "no";    }

    // Add logic for other sections here if needed

    // Only update toggles that haven't been manually modified by the user
    setAccordionToggles(prevToggles => {
      const updatedToggles = { ...prevToggles };
      Object.keys(initialToggles).forEach(sectionName => {
        if (!userModifiedToggles.current.has(sectionName)) {
          updatedToggles[sectionName] = initialToggles[sectionName];
        }
      });
      return updatedToggles;
    });
    
    setSectionDetails(initialDetails);
  }, [skidConstructionDetails, activeTabNumber, sections, pumpManifoldPipingDetails, stationEnclosureDetails, dropPipeCommentDetails, levelControlsDetails, pumpDetails, waterTreatment, fertigationDetails, skyHarvesterDetails, shipLooseDetails, shipEarlyParentDetails, jobId, apisLoading]);
  
  const location = useLocation();
  const { state } = location;
  const tbdItem = state?.tbdItem;

  const expandAccordion = useCallback((tbdItem) => {
    // Implement the logic to expand the accordion
    const sectionName = tbdItem.Table;
    const sectionIndex = sections.findIndex(section => section.name.toLowerCase() === sectionName.toLowerCase());
    if (sectionIndex !== -1) {
      setAccordionToggles(prevToggles => ({
        ...prevToggles,
        [sections[sectionIndex].name]: "yes"
      }));
    }
  }, [sections]);

  useEffect(() => {
    if (tbdItem) {
      // Logic to expand the corresponding accordion based on tbdItem
      expandAccordion(tbdItem);
    }
  }, [tbdItem, expandAccordion]);

  // Function to check if a section is marked as TBD
  const hasTbdForSection = (section) => {
    const sectionFormMap = {
      "HC Requirements": ["hcRequirements"],
      "Skid Construction": ["skidConstruction"],
      "Pump Column / Manifold / Piping": ["pumpManifoldPiping"],
      "Station Enclosure": ["stationEnclosure"],
      "Drop Pipes": ["dropPipeComments", "dropPipes"],
      "Pumps": ["pumps", "pumpDetails"],
      "Level Controls": ["levelControls", "fillSources"],
      "Water Treatment": ["waterTreatment", "inletScreen", "inletStrainer", "dischargeFilter", "dischargeStrainer", "roSystem", "uvSystem"],
      "Fertigation": ["fertigation"],
      "Sky Harvester": ["skyHarvester"],
      "Ship Loose": ["shipLoose"],
      "Ship Early/ Direct Form": ["shipEarlyParent", "shipEarlyItems"],
    };

    const formKeys = sectionFormMap[section.name] || [];
    return formKeys.some((formKey) => Boolean(tbdForms[formKey]?.hasTbd));
  };
  
  return (
    <div>
      <TopNav />
      <JobGeneralOverview />
      <JobNav keyValue={currentKey} />
      <SaveBanner showNotification={showNotification} saveStatus={saveStatus} />
      <CheckoutNotification currentUser={currentUser} />

      <ZoneButtons activeTabNumber={activeTabNumber} />

      {apisLoading && (
        <div className="text-center py-3">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading data...</span>
          </div>
          <p className="mt-2">Loading section data...</p>
        </div>
      )}

      <Accordion alwaysOpen>
        { [
          ...(isHcTrackerType
            ? [{ component: HCRequirements, name: "HC Requirements" }]
            : []),
          ...sections,
        ].map((section, index) => {
          if (!section || !section.name) {
            // Skip any undefined or invalid sections
            return null;
          }

          const SectionComponent = section.component;
          const isDisabled = section.isSpecial 
            ? false // Ship Early/ Direct Form is never disabled, but controlled by its own logic
            : (accordionToggles[section.name] === "no");
          const sectionId = sectionDetails[section.name]; // Get the ID for the current section if it exists
          const tableKey =
            section.name.charAt(0).toLowerCase() +
            section.name
              .slice(1)
              .replace(/[^a-zA-Z0-9]/g, "")
              .replace(/\s+/g, ""); // Remove special characters and spaces

          return (
            <Accordion.Item
              eventKey={String(index)}
              key={section.name}
              className={isDisabled ? "disabled-accordion" : ""}
            >
              <Accordion.Header className={isDisabled ? "disabled-header" : ""}>
                <Row className="w-100 align-items-center">
                  {/* Render toggle on the left side and keep it always accessible */}
                  {section.hasToggle && (
                    <Col xs="auto" className="d-flex align-items-center">
                      <Form.Check
                        disabled={readOnly || (!roles.Admin && !roles.Apps) || apisLoading} 
                        className="large-toggle"
                        type="switch"
                        id={`${section.name}-toggle`}
                        label=""
                        checked={accordionToggles[section.name] === "yes"}                        onChange={(e) => {
                          const newValue = e.target.checked ? "yes" : "no";
                          
                          // Mark this toggle as manually modified
                          userModifiedToggles.current.add(section.name);
                          
                          // Update local state immediately
                          setAccordionToggles((prevToggles) => ({
                            ...prevToggles,
                            [section.name]: newValue,
                          }));
                          
                          // Only make the API call if we have a section ID and APIs are done loading
                          if (sectionId && !apisLoading) {
                            console.log("Toggle clicked in JobMechInfo:", {
                              sectionId: sectionId,
                              section: section.name,
                              field: section.field,
                              newValue: newValue,
                              apiUrl: section.apiUrl
                            });
                            
                            handleBlur(
                              {
                                target: {
                                  name: section.name,
                                  value: newValue === "yes", // Pass as boolean 
                                  type: "switch",
                                },
                              },
                              section.field, // Use field instead of section.name for consistency
                              [],
                              {
                                ID: sectionId,
                                url: section.apiUrl,
                                field: section.field,
                                tableKey: tableKey,
                                zone: activeTabNumber,
                              }
                            );
                          }
                        }}
                        style={{ pointerEvents: "auto" }}
                      />
                    </Col>
                  )}
                  {!section.hasToggle && <Col xs="auto" />}{" "}
                  {/* Keeps column structure intact */}
                  <Col>
                    <h3>
                      {hasTbdForSection(section) && (
                        <i
                          className="bi bi-exclamation-diamond-fill"
                          style={{ color: "red" }}
                        />
                      )}{" "}
                      {section.name}
                      {section.name === "Ship Loose" && accordionToggles[section.name] === "yes" && getZoneItemCount(shipLooseDetails, jobId)}
                      {section.name === "Ship Early/ Direct Form" && getShipEarlyItemCount(shipEarlyParentDetails, jobId)}
                    </h3>
                  </Col>
                </Row>
              </Accordion.Header>
              {(!isDisabled || section.isSpecial) && (
                <Accordion.Body>
                  {/* Info icon for Ship Loose and Ship Early/ Direct Form */}
                  {(section.name === "Ship Loose" || section.name === "Ship Early/ Direct Form") && (
                    <div className="d-flex justify-content-start mb-3">
                      <i 
                        className="bi bi-info-circle text-primary" 
                        style={{ cursor: 'pointer', fontSize: '1rem' }}
                        onClick={() => {
                          setInfoBubbleSection({
                            sectionName: section.name.replace(/\s+/g, ''),
                            value: 'Page',
                            title: `${section.name} Information`
                          });
                          setShowInfoBubbleModal(true);
                        }}
                        title="Click for information"
                      ></i>
                    </div>
                  )}
                  
                  {!section.isSpecial && (
                    <h5 className="text-center text-muted mb-3">
                      {activeTabNumber === 1 ? "Zone 1" : "Zone 2"}
                    </h5>
                  )}
                  {loadingSections[section.name] ? (
                    <div className="d-flex justify-content-center align-items-center">
                      <div className="spinner-border" role="status">
                        <span className="visually-hidden">Loading...</span>
                      </div>
                    </div>
                  ) : (
                    SectionComponent && <SectionComponent />
                  )}
                </Accordion.Body>
              )}
            </Accordion.Item>
          );
        })}
      </Accordion>

      {/* Info Bubble Modal */}
      <InfoBubbleModal
        show={showInfoBubbleModal}
        onHide={() => setShowInfoBubbleModal(false)}
        sectionName={infoBubbleSection.sectionName}
        value={infoBubbleSection.value}
        title={infoBubbleSection.title}
        displayAsOrderedList={false}
      />
    </div>
  );
}

export default JobMechSpecs;
